import { ButtonLink } from '../components/Button';
import { Reveal } from '../components/Reveal';
import { Accent, SectionHeading } from '../components/SectionHeading';
import { site } from '../content/site';
import { additionalSkills, education, roles } from '../content/experience';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

export default function Resume() {
  useDocumentTitle('Resumé');

  return (
    <div className="bg-gradient-to-b from-mint-100 to-cream">
      <div className="mx-auto max-w-3xl px-6 py-16 sm:px-8 sm:py-20">
        <Reveal>
          <SectionHeading eyebrow="Resumé">
            Doing your <Accent>homework</Accent> on me.
          </SectionHeading>
        </Reveal>

        <Reveal delay={90}>
          <div className="mt-10 rounded-blob bg-white px-8 py-8 text-center shadow-soft">
            <p className="font-display text-xl font-semibold text-ink">{site.firstName}</p>
            <p className="mt-1 text-muted">{site.location}</p>
            <a
              href={`mailto:${site.email}`}
              className="mt-3 inline-block font-display font-semibold text-mint-700 underline decoration-mint-300 decoration-2 underline-offset-4 transition-colors hover:text-mint-900"
            >
              {site.email}
            </a>
          </div>
        </Reveal>

        <section className="mt-16">
          <Reveal>
            <h3 className="font-display text-2xl">Professional experience</h3>
          </Reveal>

          <ol className="relative mt-8 space-y-8 border-l-2 border-dashed border-mint-200">
            {roles.map((role, index) => (
              <Reveal
                key={`${role.employer}-${role.period}`}
                delay={index * 70}
                as="li"
                className="relative pl-8"
              >
                <span
                  aria-hidden="true"
                  className="absolute -left-[9px] mt-2 h-4 w-4 rounded-full border-4 border-cream bg-mint-400"
                />
                <p className="font-display text-sm font-semibold tracking-wide text-blush-500">
                  {role.period}
                </p>
                <h4 className="mt-1 text-lg">{role.title}</h4>
                <p className="text-sm text-muted">
                  {role.employer} · {role.location}
                </p>
                <ul className="mt-3 space-y-2">
                  {role.bullets.map((bullet) => (
                    <li key={bullet} className="flex gap-3 leading-relaxed">
                      <span aria-hidden="true" className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-mint-300" />
                      <span>{bullet}</span>
                    </li>
                  ))}
                </ul>
              </Reveal>
            ))}
          </ol>
        </section>

        <div className="mt-16 grid gap-6 sm:grid-cols-2">
          <Reveal>
            <section className="h-full rounded-blob bg-white p-8 shadow-soft">
              <h3 className="font-display text-xl">Education</h3>
              <p className="mt-4 font-display font-semibold text-ink">{education.school}</p>
              <p className="text-sm text-muted">{education.location}</p>
              <p className="text-sm text-muted">{education.period}</p>
              <p className="mt-3 leading-relaxed">{education.detail}</p>
            </section>
          </Reveal>

          <Reveal delay={110}>
            <section className="h-full rounded-blob bg-white p-8 shadow-soft">
              <h3 className="font-display text-xl">Additional skills</h3>
              <ul className="mt-4 space-y-2">
                {additionalSkills.map((skill) => (
                  <li key={skill} className="flex gap-3 leading-relaxed">
                    <span aria-hidden="true" className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-blush-300" />
                    <span>{skill}</span>
                  </li>
                ))}
              </ul>
            </section>
          </Reveal>
        </div>

        <Reveal delay={90}>
          <div className="mt-14 text-center">
            <p className="text-sm text-muted">
              References available upon request. A more informational resumé can be acquired by{' '}
              <a
                href={`mailto:${site.email}`}
                className="font-semibold text-mint-700 underline decoration-mint-300 decoration-2 underline-offset-4 transition-colors hover:text-mint-900"
              >
                emailing me
              </a>
              .
            </p>
            <div className="mt-8">
              <ButtonLink to="/contact" variant="blush">
                💬 Let&apos;s start a dialog
              </ButtonLink>
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
